import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { ArrowUpRight, Menu, X, MapPin, Phone, Mail } from "lucide-react";
import { Logo } from "@/components/Logo";

const LINKS = [
  ["Home", "/"], ["Our Company", "/company"], ["Products & Capabilities", "/products"],
  ["Manufacturing & Quality", "/manufacturing"], ["Defense, Oil & Gas & Heavy Industry", "/defense"],
];

export function SiteNav() {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  const go = (to) => { setOpen(false); if (to === loc.pathname) window.scrollTo({ top: 0, behavior: "smooth" }); else nav(to); };
  const quote = () => {
    setOpen(false);
    if (loc.pathname === "/") document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" });
    else nav("/", { state: { scrollTo: "quote" } });
  };

  return (
    <header className="fixed top-0 inset-x-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button onClick={() => go("/")} data-testid="logo-home"><Logo /></button>
        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map(([label, to], i) => (
            <button key={i} data-testid={`nav-${to}`} onClick={() => go(to)}
              className={`font-head font-semibold text-[12px] tracked-sm uppercase transition-colors duration-300 ${loc.pathname === to && to !== "/" ? "text-[#D4AF37]" : "text-[#0A192F]/70 hover:text-[#0A192F]"}`}>
              {label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-8">
          <button onClick={quote} data-testid="nav-quote-btn"
            className="btn-gold hidden sm:inline-flex items-center gap-2 font-head font-bold text-[13px] tracked-sm uppercase px-6 py-3 rounded-lg">
            Get Precision Quote <ArrowUpRight size={16} />
          </button>
          <button className="lg:hidden text-[#0A192F]" onClick={() => setOpen(!open)} data-testid="mobile-toggle">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-6 py-5 flex flex-col gap-4">
          {LINKS.map(([label, to], i) => (
            <button key={i} onClick={() => go(to)} className="text-left font-head font-semibold text-sm uppercase tracked-sm text-[#0A192F]/70">{label}</button>
          ))}
          <button onClick={quote} className="btn-gold mt-2 font-head font-bold text-sm tracked-sm uppercase px-6 py-3 rounded-lg">Get Precision Quote</button>
        </div>
      )}
    </header>
  );
}

export function SiteFooter() {
  const nav = useNavigate();
  return (
    <footer className="bg-[#0A192F] text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <Logo light />
          <p className="font-head font-bold text-lg mt-6 leading-snug">
            <span className="silver-text">Driven by Precision. Powered by Trust </span><span className="gold-text">&amp; Global Partnerships.</span>
          </p>
          <p className="text-slate-400 mt-4 max-w-sm leading-relaxed">Your enterprise partner for supply chain, precision engineering and quality-verified component sourcing.</p>
        </div>
        <div className="md:col-span-3">
          <div className="font-head text-[11px] tracked uppercase text-[#D4AF37] mb-4">Office</div>
          <div className="flex items-start gap-2 text-slate-300 text-sm leading-relaxed">
            <MapPin size={16} className="text-[#D4AF37] mt-0.5 shrink-0" />
            <span>Suite 329, 17 Worthington Ave<br />Brampton, ON L7A 2Y7, Canada</span>
          </div>
          <a href="tel:+12899466435" className="flex items-center gap-2 mt-4 text-slate-300 text-sm hover:text-[#D4AF37] transition-colors">
            <Phone size={16} className="text-[#D4AF37] shrink-0" />+1-289-946-6435
          </a>
          <a href="mailto:info@exonglobal.ca" className="flex items-center gap-2 mt-1 text-slate-300 text-sm hover:text-[#D4AF37] transition-colors">
            <Mail size={16} className="text-[#D4AF37] shrink-0" />info@exonglobal.ca
          </a>
        </div>
        <div className="md:col-span-4">
          <div className="font-head text-[11px] tracked uppercase text-[#D4AF37] mb-4">Navigate</div>
          <div className="grid grid-cols-2 gap-2">
            {LINKS.map(([l, to], i) => (
              <button key={i} onClick={() => nav(to)} className="text-left text-slate-300 hover:text-white transition-colors text-sm">{l}</button>
            ))}
          </div>
          <Link to="/admin/login" className="inline-block mt-6 text-slate-500 hover:text-[#D4AF37] transition-colors text-sm">Admin login →</Link>
        </div>
      </div>
      <div className="border-t border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <p className="text-slate-500 text-[11px] leading-relaxed mb-4 max-w-4xl">Exon Global operates as the authorised regional promotion, technical interface and distribution partner for Ratna Gears Pvt. Ltd. and other manufacturers. All trademarks, brand names and client logos are the property of their respective owners and are shown for reference only.</p>
          <div className="flex flex-wrap justify-between gap-4 text-slate-500 text-xs">
            <span>© {new Date().getFullYear()} Exon Global. All rights reserved.</span>
            <div className="flex gap-6">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">Legal Disclosures</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

export function PageHero({ eyebrow, title, sub }) {
  return (
    <section className="relative bg-[#0A192F] text-white pt-36 pb-20 overflow-hidden">
      <div className="absolute inset-0 hero-lines" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <span className="font-head font-bold tracked uppercase text-xl lg:text-2xl gold-text">{eyebrow}</span>
        <h1 className="font-head font-extrabold tracked uppercase text-3xl lg:text-5xl mt-4 leading-tight max-w-4xl">{title}</h1>
        {sub && <p className="text-slate-300 mt-6 max-w-2xl leading-relaxed">{sub}</p>}
      </div>
    </section>
  );
}
